import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from "react";
import { ArrowDown, ArrowUp, ChevronDown, Filter, X, EyeOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../utils";
import { Button } from "../../base/button/button";
import { Input } from "../../base/input/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../../base/dropdown-menu/dropdown-menu";
import { Popover, PopoverAnchor, PopoverContent } from "../../base/popover/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../base/select/select";
/**
 * A sortable and filterable column header component for DataTable.
 * Uses a dropdown menu pattern for a cleaner, more intuitive UX.
 *
 * UX Pattern:
 * - Click header → Opens dropdown menu with sort, filter, and visibility options
 * - "Add filter" creates a filter chip in the toolbar (not a nested popover)
 * - Reduces visual clutter from separate buttons
 * - Groups related actions logically
 * - Follows patterns used by GitHub, Linear, Notion
 *
 * @example
 * ```tsx
 * <DataTableColumnHeader
 *   column={column}
 *   title="Name"
 * />
 * ```
 */ export function DataTableColumnHeader({ column, title, className, onAddFilter, onUpdateFilter, externallyFiltered = false, sortedColumns = 0 }) {
    const { t } = useTranslation();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
    const [filterDraft, setFilterDraft] = useState("");
    const canSort = column.getCanSort();
    const canFilter = column.getCanFilter();
    const filterType = column.columnDef.meta?.filterType;
    const filterOptions = column.columnDef.meta?.filterOptions;
    const isSorted = column.getIsSorted();
    // Several columns sorted (Malte, 30.10.2026): the header shows its rank, and
    // a column not yet in the order may join it after the others.
    const sortRank = sortedColumns > 1 && isSorted ? column.getSortIndex() + 1 : null;
    const canJoinSort = !isSorted && sortedColumns > 0 && column.getCanMultiSort();
    const isFiltered = column.getIsFiltered() || externallyFiltered;
    // A column tied to the host's filter bar offers „Filter“ without a chip type of its own.
    const canRequestFilter = !!column.columnDef.meta?.filterField && !!onAddFilter;
    const offersFilter = canFilter && !!filterType || canRequestFilter;
    // The value editor opens anchored to the header the user clicked, so the
    // filter never appears "somewhere else" (the toolbar chip alone was hard to
    // find). Only text/select fit a small anchored editor; range types keep the
    // chip-only flow.
    const anchoredEditorAvailable = !!onUpdateFilter && (filterType === "text" || filterType === "select" && !!filterOptions?.length);
    const handleAddFilterClick = ()=>{
        onAddFilter?.(column.id);
        setDropdownOpen(false);
        if (anchoredEditorAvailable) {
            setFilterDraft("");
            setFilterPopoverOpen(true);
        }
    };
    const fieldPlaceholder = t("dataTable.filterValuePlaceholder", {
        defaultValue: "Enter {{label}}...",
        label: title
    });
    // If neither sortable nor filterable, just show title
    if (!canSort && !canFilter && !canRequestFilter) {
        return /*#__PURE__*/ _jsx("div", {
            className: cn("text-xs font-semibold", className),
            children: title
        });
    }
    const getHeaderIcon = ()=>{
        if (isSorted === "desc") return ArrowDown;
        if (isSorted === "asc") return ArrowUp;
        return ChevronDown;
    };
    const HeaderIcon = getHeaderIcon();
    return /*#__PURE__*/ _jsxs(Popover, {
        open: filterPopoverOpen,
        onOpenChange: setFilterPopoverOpen,
        children: [
            /*#__PURE__*/ _jsx(PopoverAnchor, {
                asChild: true,
                children: /*#__PURE__*/ _jsx("div", {
                    className: cn("flex items-center space-x-1", className),
                    children: /*#__PURE__*/ _jsxs(DropdownMenu, {
                        open: dropdownOpen,
                        onOpenChange: setDropdownOpen,
                        children: [
                            /*#__PURE__*/ _jsx(DropdownMenuTrigger, {
                                asChild: true,
                                children: /*#__PURE__*/ _jsxs(Button, {
                                    variant: "ghost",
                                    size: "small",
                                    className: cn("-ml-3 h-8 hover:bg-surface-strong data-[state=open]:bg-surface-strong", (isSorted || isFiltered) && "font-medium"),
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            children: title
                                        }),
                                        /*#__PURE__*/ _jsx(HeaderIcon, {
                                            className: "ml-2 h-4 w-4"
                                        }),
                                        sortRank !== null && /*#__PURE__*/ _jsx("span", {
                                            className: "text-[10px] leading-none text-fg-secondary",
                                            "aria-label": t("dataTable.sortRank", {
                                                defaultValue: "Sort order {{rank}}",
                                                rank: sortRank
                                            }),
                                            children: sortRank
                                        }),
                                        isFiltered && /*#__PURE__*/ _jsx("span", {
                                            className: "ml-1 h-2 w-2 rounded-full bg-info-solid",
                                            "aria-label": t("dataTable.filtered")
                                        })
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsxs(DropdownMenuContent, {
                                align: "start",
                                className: "w-[180px]",
                                children: [
                                    canSort && /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsxs(DropdownMenuItem, {
                                                onClick: ()=>{
                                                    column.toggleSorting(false);
                                                    setDropdownOpen(false);
                                                },
                                                children: [
                                                    /*#__PURE__*/ _jsx(ArrowUp, {
                                                        className: "mr-2 h-4 w-4"
                                                    }),
                                                    t("dataTable.sortAscending")
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs(DropdownMenuItem, {
                                                onClick: ()=>{
                                                    column.toggleSorting(true);
                                                    setDropdownOpen(false);
                                                },
                                                children: [
                                                    /*#__PURE__*/ _jsx(ArrowDown, {
                                                        className: "mr-2 h-4 w-4"
                                                    }),
                                                    t("dataTable.sortDescending")
                                                ]
                                            }),
                                            canJoinSort && /*#__PURE__*/ _jsxs(_Fragment, {
                                                children: [
                                                    /*#__PURE__*/ _jsxs(DropdownMenuItem, {
                                                        onClick: ()=>{
                                                            column.toggleSorting(false, true);
                                                            setDropdownOpen(false);
                                                        },
                                                        children: [
                                                            /*#__PURE__*/ _jsx(ArrowUp, {
                                                                className: "mr-2 h-4 w-4"
                                                            }),
                                                            t("dataTable.sortThenAscending", {
                                                                defaultValue: "Then ascending"
                                                            })
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsxs(DropdownMenuItem, {
                                                        onClick: ()=>{
                                                            column.toggleSorting(true, true);
                                                            setDropdownOpen(false);
                                                        },
                                                        children: [
                                                            /*#__PURE__*/ _jsx(ArrowDown, {
                                                                className: "mr-2 h-4 w-4"
                                                            }),
                                                            t("dataTable.sortThenDescending", {
                                                                defaultValue: "Then descending"
                                                            })
                                                        ]
                                                    })
                                                ]
                                            }),
                                            isSorted && /*#__PURE__*/ _jsxs(DropdownMenuItem, {
                                                onClick: ()=>{
                                                    column.clearSorting();
                                                    setDropdownOpen(false);
                                                },
                                                children: [
                                                    /*#__PURE__*/ _jsx(X, {
                                                        className: "mr-2 h-4 w-4"
                                                    }),
                                                    t("dataTable.clearSort")
                                                ]
                                            })
                                        ]
                                    }),
                                    canSort && offersFilter && /*#__PURE__*/ _jsx(DropdownMenuSeparator, {}),
                                    offersFilter && /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsxs(DropdownMenuItem, {
                                                onClick: handleAddFilterClick,
                                                children: [
                                                    /*#__PURE__*/ _jsx(Filter, {
                                                        className: "mr-2 h-4 w-4"
                                                    }),
                                                    t("dataTable.addFilter")
                                                ]
                                            }),
                                            column.getIsFiltered() && /*#__PURE__*/ _jsxs(DropdownMenuItem, {
                                                onClick: ()=>{
                                                    column.setFilterValue(undefined);
                                                    setDropdownOpen(false);
                                                },
                                                children: [
                                                    /*#__PURE__*/ _jsx(X, {
                                                        className: "mr-2 h-4 w-4"
                                                    }),
                                                    t("dataTable.clearFilter")
                                                ]
                                            })
                                        ]
                                    }),
                                    offersFilter && /*#__PURE__*/ _jsx(DropdownMenuSeparator, {}),
                                    /*#__PURE__*/ _jsxs(DropdownMenuItem, {
                                        onClick: ()=>{
                                            column.toggleVisibility(false);
                                            setDropdownOpen(false);
                                        },
                                        children: [
                                            /*#__PURE__*/ _jsx(EyeOff, {
                                                className: "mr-2 h-4 w-4"
                                            }),
                                            t("dataTable.hideColumn")
                                        ]
                                    })
                                ]
                            })
                        ]
                    })
                })
            }),
            anchoredEditorAvailable && /*#__PURE__*/ _jsx(PopoverContent, {
                align: "start",
                className: "w-56 p-2",
                children: filterType === "text" ? /*#__PURE__*/ _jsx(Input, {
                    type: "text",
                    autoFocus: true,
                    value: filterDraft,
                    onChange: (e)=>{
                        setFilterDraft(e.target.value);
                        onUpdateFilter?.(column.id, e.target.value);
                    },
                    onKeyDown: (e)=>{
                        if (e.key === "Enter") setFilterPopoverOpen(false);
                    },
                    placeholder: fieldPlaceholder,
                    className: "h-8 text-sm"
                }) : /*#__PURE__*/ _jsxs(Select, {
                    value: filterDraft,
                    onValueChange: (value)=>{
                        setFilterDraft(value);
                        onUpdateFilter?.(column.id, value);
                        setFilterPopoverOpen(false);
                    },
                    children: [
                        /*#__PURE__*/ _jsx(SelectTrigger, {
                            className: "h-8 text-sm",
                            children: /*#__PURE__*/ _jsx(SelectValue, {
                                placeholder: t("dataTable.selectPlaceholder", {
                                    defaultValue: "Select..."
                                })
                            })
                        }),
                        /*#__PURE__*/ _jsx(SelectContent, {
                            children: filterOptions?.map((option)=>/*#__PURE__*/ _jsx(SelectItem, {
                                    value: option.value,
                                    children: option.label
                                }, option.value))
                        })
                    ]
                })
            })
        ]
    });
}

//# sourceMappingURL=DataTableColumnHeader.js.map